import { Vector3d } from "../math/vector3d";
import type { SpacecraftState } from "../spacecraft/spacecraft_state";

/**
 * A planned maneuver.
 */
export interface Maneuver {
  id: string;
  /** When to execute (Julian Date) */
  timeJD: number;
  /** Delta-V in km/s */
  deltaV: Vector3d;
  /** true = instantaneous, false = finite burn */
  isImpulsive: boolean;
  /** Burn duration in seconds (for finite burns) */
  duration: number;
}

/** Delta-V magnitude in m/s */
export function maneuverDeltaVMagnitude(m: Maneuver): number {
  return m.deltaV.magnitude * 1000.0;
}

/**
 * Execute an impulsive maneuver: add delta-V to velocity, deduct fuel.
 * @returns Actual delta-V applied in m/s (may be less if fuel runs out)
 */
export function executeImpulsive(spacecraft: SpacecraftState, maneuver: Maneuver): number {
  return spacecraft.applyImpulsiveBurn(maneuver.deltaV);
}

/**
 * Compute fuel required for a maneuver.
 */
export function fuelRequired(spacecraft: SpacecraftState, maneuver: Maneuver): number {
  if (!spacecraft.engine) return 0;
  return spacecraft.engine.fuelForDeltaV(maneuverDeltaVMagnitude(maneuver), spacecraft.totalMass);
}

/**
 * Check if spacecraft has enough fuel for a maneuver.
 */
export function canExecuteManeuver(spacecraft: SpacecraftState, maneuver: Maneuver): boolean {
  return fuelRequired(spacecraft, maneuver) <= spacecraft.fuelMass;
}

/**
 * Queue of planned maneuvers, sorted by execution time.
 */
export class ManeuverQueue {
  private maneuvers: Maneuver[] = [];
  private nextId: number = 0;

  get count(): number {
    return this.maneuvers.length;
  }

  get all(): readonly Maneuver[] {
    return this.maneuvers;
  }

  /**
   * Add a maneuver to the queue. Maintains sorted order by time.
   */
  add(maneuver: Maneuver): string {
    if (!maneuver.id) {
      maneuver.id = `maneuver_${this.nextId++}`;
    }

    let insertIndex = 0;
    for (let i = 0; i < this.maneuvers.length; i++) {
      if (this.maneuvers[i].timeJD > maneuver.timeJD) break;
      insertIndex = i + 1;
    }

    this.maneuvers.splice(insertIndex, 0, maneuver);
    return maneuver.id;
  }

  /** Remove a maneuver by ID. */
  remove(id: string): boolean {
    const idx = this.maneuvers.findIndex((m) => m.id === id);
    if (idx >= 0) {
      this.maneuvers.splice(idx, 1);
      return true;
    }
    return false;
  }

  /**
   * Get the next maneuver that should be executed at or before the given time.
   * Returns undefined if no maneuver is due.
   */
  getNextDue(currentTimeJD: number): Maneuver | undefined {
    if (this.maneuvers.length === 0) return undefined;
    if (this.maneuvers[0].timeJD <= currentTimeJD) return this.maneuvers[0];
    return undefined;
  }

  /** Remove and return the next due maneuver. */
  popNextDue(currentTimeJD: number): Maneuver | undefined {
    const next = this.getNextDue(currentTimeJD);
    if (next) this.maneuvers.splice(0, 1);
    return next;
  }

  /** Clear all maneuvers. */
  clear(): void {
    this.maneuvers = [];
  }
}
